'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';

/**
 * One-time welcome banner shown on / for users who:
 *   - are signed in,
 *   - haven't dismissed before (localStorage flag keyed by user.id),
 *   - registered within the last 7 days (so returning veterans don't see it).
 *
 * Three CTAs land them in the busiest discovery surfaces. Dismiss persists
 * per-user — switch accounts and the new account gets its own welcome.
 */
const KEY_PREFIX = 'ps:welcome:dismissed:';
const NEW_USER_DAYS = 7;

export default function WelcomeBanner() {
  const { user } = useAuth();
  const t = useT();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) { setShow(false); return; }
    try {
      if (localStorage.getItem(KEY_PREFIX + user.id) === '1') return;
    } catch { /* localStorage blocked → fall through, show banner */ }
    const ageDays = (Date.now() - new Date(user.created_at).getTime()) / 86400000;
    if (ageDays > NEW_USER_DAYS) return;
    setShow(true);
  }, [user]);

  if (!show || !user) return null;

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(KEY_PREFIX + user.id, '1'); } catch { /* ignore */ }
  };

  return (
    <div
      role="region"
      aria-label={t('welcome.eyebrow')}
      style={{
        margin: '0 0 24px',
        padding: '20px 24px',
        background: 'linear-gradient(135deg, color-mix(in oklch, var(--accent) 14%, transparent), color-mix(in oklch, var(--accent) 4%, transparent))',
        border: '1px solid color-mix(in oklch, var(--accent) 35%, transparent)',
        borderRadius: 'var(--radius-lg)',
        display: 'flex', gap: 16, alignItems: 'flex-start',
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 24, color: 'var(--accent)', flexShrink: 0 }}>
        waving_hand
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 4,
        }}>
          {t('welcome.eyebrow')}
        </div>
        <div style={{
          fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 600,
          letterSpacing: '-0.01em', marginBottom: 6,
        }}>
          {t('welcome.headline', { name: user.name.split(' ')[0] })}
        </div>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 14, maxWidth: 600 }}>
          {t('welcome.body')}
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link href="/trending" className="ps-btn ps-btn-primary ps-btn-sm">
            <span className="material-symbols-outlined ps-icon">local_fire_department</span>
            {t('welcome.cta_trending')}
          </Link>
          <Link href="/explore" className="ps-btn ps-btn-ghost ps-btn-sm">
            <span className="material-symbols-outlined ps-icon">explore</span>
            {t('welcome.cta_explore')}
          </Link>
          <Link href="/create" className="ps-btn ps-btn-ghost ps-btn-sm">
            <span className="material-symbols-outlined ps-icon">add</span>
            {t('welcome.cta_share')}
          </Link>
        </div>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label={t('welcome.dismiss_aria')}
        title={t('welcome.dismiss_title')}
        style={{
          background: 'transparent', border: 0, color: 'var(--ink-3)',
          cursor: 'pointer', padding: 4, lineHeight: 1, flexShrink: 0,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
      </button>
    </div>
  );
}
